import React, { useRef, useState, useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import NavLinks from '../NavLinks/NavLinks'
import AskMeetingDropdown from '../Dropdown/AskMeetingDropdown'
import WhatIsThis from './WhatIsThis'
import Loader from '../Loader/Loader'
import Feedback from '../Feedback/Feedback'
import AuthenticatedRoutes from '../../Utils/AuthenticatedRoutes'
import { ask } from '../../Api/search'
import { fetctPreviousQuestions } from '../../Api/transcriptAndSummary'
import { fetchUserTags } from '../../Api/transcriptAndSummary'
import { fetchLastMeeting, fetchPreviousMeetings } from '../../Api/meetings'
import './ask.css'
import logAppEvent from '../../Api/appEventsLogging'
import { useSelector } from 'react-redux'
import NavbarOpen from '../NavLinks/NavbarOpen'

const monthsShort = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const Ask = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const tagFilterButtonRef = useRef(null)
  const meetingFilterButtonRef = useRef(null)
  const eventButtonRef = useRef(null)
  const [searchWithin, setSearchWithin] = useState('my own calls')
  const [questionId, setQuestionId] = useState(null)
  const [selectedTagFilters, setSelectedTagFilters] = useState([])
  const [selectedMeetingFilters, setSelectedMeetingFilters] = useState([''])
  const [lastMeeting, setLastMeeting] = useState('')
  const [search, setSearch] = useState('')
  const [showWhatIsThis, setShowWhatIsThis] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [tags, setTags] = useState([])
  const [answers, setAnswers] = useState([])
  const [previousMeetings, setPreviousMeetings] = useState([])
  const [eventsData, setEventsData] = useState([])
  const [selectedEvent, setSelectedEvent] = useState([])
  const [isMeeting, setIsMeeting] = useState(false)
  const user = useSelector((state) => state?.user?.data)

  const fetctPreviousQuestionsData = useCallback(async () => {
    logAppEvent('ask_see_previous_q_click', {
      user_id: user?.id,
    })
    setQuestionId(null)
    const apiResponse = await fetctPreviousQuestions()
    if (apiResponse?.length > 0) {
      setAnswers([...apiResponse])
    } else {
      setAnswers([])
    }
    setSearch('')
  }, [])
  const fetchUserTagsData = useCallback(async () => {
    const apiResponse = await fetchUserTags()
    if (apiResponse?.length > 0) {
      setTags([
        ...apiResponse.map((item) => ({
          value: item.id,
          label: item.name,
        })),
      ])
    } else {
      setTags([])
    }
  }, [])

  const fetchLastMeetingData = useCallback(async () => {
    const apiResponse = await fetchLastMeeting()
    if (apiResponse?.title) {
      setLastMeeting(apiResponse.title)
    } else { 
      setLastMeeting('')
    }
  }, [])

  const fetchPreviousMeetingsData = useCallback(async () => {
    const apiResponse = await fetchPreviousMeetings()
    if (apiResponse?.length > 0) {
      const temp = []
      apiResponse.forEach((event) => {
        let tempDate = Number(event.start_time)
          ? new Date(Number(event.start_time))
          : new Date()
        let startTimeArray = new Date(Number(event.start_time))
          .toString()
          .split(' ')
        let endTimeArray = new Date(Number(event.end_time))
          .toString()
          .split(' ')
        let startTime = startTimeArray[4].split(':')
        let endTime = endTimeArray[4].split(':')
        let date = `${startTimeArray[1]} ${
          startTimeArray[2]
        },'${startTimeArray[3].substring(startTimeArray[3].length - 2)}`
        let time = `${date} ${startTime[0] + ':' + startTime[1]}-${
          endTime[0] + ':' + endTime[1]
        } ${
          parseInt(startTime[0]) * 60 + parseInt(startTime[1]) < 360
            ? 'AM'
            : 'PM'
        }`
        temp.push({
          value: event.event_id,
          label: `${event.title} 📅 ${time} 👥 ${event.attendees}`,
          title: `${event.title} (${
            monthsShort[tempDate.getMonth()]
          } ${tempDate.getDate()}, ${tempDate.getFullYear()})`,
        })
      })
      setPreviousMeetings([...temp])
    } else {
      setPreviousMeetings([])
    }
  }, [])

  useEffect(() => {
    fetchLastMeetingData()
    fetchPreviousMeetingsData()
    fetchUserTagsData()
  }, [fetchUserTagsData, fetchLastMeetingData, fetchPreviousMeetingsData])

  useEffect(() => {
    if (searchParams?.get('tab') === 'new') {
      setSearch('')
      setAnswers([])
      navigate('/ask')
    }
  }, [searchParams, navigate])

  const submitHandler = useCallback(
    async (event) => {
      event.preventDefault()
      logAppEvent('ask_ask_arrowhead_q_search', {
        user_id: user?.id,
      })
      if (search.trim()) {
        setIsLoading(true)
        const apiResponse = await ask(
          search.trim(),
          selectedTagFilters,
          selectedMeetingFilters
        )
        if (apiResponse?.questionId) {
          setQuestionId(apiResponse.questionId)
        } else {
          setQuestionId(null)
        }
        if (apiResponse && apiResponse?.answer) {
          // setAnswers([...apiResponse?.answer])
          setAnswers([...apiResponse.answer])
        } else {
          setAnswers([])
        }
        if (apiResponse?.events?.length > 0) {
          const temp = []
          apiResponse.events.forEach((event) => {
            let startTimeArray = new Date(Number(event.start_time))
              .toString()
              .split(' ')
            let endTimeArray = new Date(Number(event.end_time)).toString().split(' ')
            let startTime = startTimeArray[4].split(':')
            let endTime = endTimeArray[4].split(':')
            let date = `${startTimeArray[1]} ${
              startTimeArray[2]
            },'${startTimeArray[3].substring(startTimeArray[3].length - 2)}`
            let time = `${date} ${startTime[0] + ':' + startTime[1]}-${
              endTime[0] + ':' + endTime[1]
            } ${
              parseInt(startTime[0]) * 60 + parseInt(startTime[1]) < 360
                ? 'AM'
                : 'PM'
            }`
            temp.push({
              value: event.transcriptId
                ? `/transcripts-summaries/transcript?eventId=${event.event_id}&transcriptId=${event.transcriptId}`
                : `/transcripts-summaries/transcript?eventId=${event.event_id}`,
              label: `${event.title} 📅 ${time} 👥 ${event.attendees}`,
            })
          })
          setEventsData([...temp])
        } else {
          setEventsData([])
        }
        setIsLoading(false)
      }
    },
    [
      setQuestionId,
      setAnswers,
      selectedMeetingFilters,
      search,
      setIsLoading,
      selectedTagFilters,
      setEventsData,
    ]
  )

  const removeTagFilters = useCallback(
    (index) => {
      const temp = [...selectedTagFilters]
      temp.splice(index, 1)
      setSelectedTagFilters([...temp])
    },
    [setSelectedTagFilters, selectedTagFilters]
  )

  useEffect(() => {
    if (selectedEvent) {
      navigate(selectedEvent)
    }
  }, [selectedEvent, navigate, setSelectedEvent])

  const handleSearchWithinClick = () => {
    logAppEvent('ask_search_within_click', {
      user_id: user?.id,
    })
  }

  const handleClick = () => {
    logAppEvent('ask_what_this_click', {
      user_id: user?.id,
    })
    setShowWhatIsThis(true)
  }

  const handleAskNewQuestion = () => {
    logAppEvent('ask_ask_new_q_from_previous_q_click', {
      user_id: user?.id,
    })
    setAnswers([])
    setSearch('')
    setQuestionId(null)
  }

  return (
    <div className='container container_light ask'>
      <AuthenticatedRoutes auth={true} />
      {showWhatIsThis && <WhatIsThis close={setShowWhatIsThis} />}
      <NavbarOpen />
      <div
        className={
          answers.length > 0 ? 'search_section active' : 'search_section'
        }
      >
        <div className='logo'>
          <img src='/assets/images/logo.png' alt='' />
        </div>
        <h1>Ask Arrowhead</h1>
        <button onClick={handleClick}>What is this?</button>
        {answers.length < 1 && (
          <>
            <form onSubmit={submitHandler}>
              <div className='filter'>
                <button
                  className='meetings-filter-button'
                  type='button'
                  ref={meetingFilterButtonRef}
                  onClick={handleSearchWithinClick}
                >
                  <span onClick={() => setIsMeeting(false)}>
                    Searching within{' '}
                    <b>
                      {selectedMeetingFilters?.filter((item) => item !== '')
                        .length > 0
                        ? 'meetings: '
                        : `${searchWithin}: `}
                    </b>
                  </span>
                  <b onClick={() => setIsMeeting(true)}>
                    {selectedMeetingFilters.filter((value) => value !== '')
                      ?.length > 0
                      ? `${selectedMeetingFilters
                        .slice(0, 2)
                        .map(
                          (meeting) =>
                            previousMeetings.filter(
                              (item) => meeting === item.value
                            )[0]?.title
                        )
                        .join(', ')} ${
                        selectedMeetingFilters.length > 2
                          ? `+${selectedMeetingFilters.length - 2}`
                          : ''
                      }`
                      : ''}
                  </b>
                </button>
                <AskMeetingDropdown
                  isMeeting={isMeeting}
                  setSearchWithin={setSearchWithin}
                  searchWithin={searchWithin}
                  isSearchable={true}
                  isMultiple={true}
                  data={previousMeetings}
                  setActiveFilters={setSelectedMeetingFilters}
                  activeFilters={selectedMeetingFilters}
                  ref={meetingFilterButtonRef}
                />
              </div>
              <input
                className='search_input'
                placeholder={`What was discussed in my last ${lastMeeting} meeting?`}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                }}
              />
            </form>

            {/* <div className="tag-filters">
              {selectedTagFilters &&
                selectedTagFilters.length > 0 &&
                selectedTagFilters.indexOf("") === -1 && (
                  <ul>
                    {selectedTagFilters.map((tag, index) => (
                      <li key={tag}>
                        <span>
                          {
                            tags?.filter(
                              (item) => item.value !== "" && item.value === tag
                            )[0]?.label
                          }
                        </span>
                        <button onClick={() => removeTagFilters(index)}>
                          x
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              <div className="filter">
                <button className="tag-filter-button" ref={tagFilterButtonRef}>
                  Search within tag
                </button>
                <Dropdown
                  isSearchable={true}
                  isMultiple={true}
                  data={tags}
                  setActiveFilters={setSelectedTagFilters}
                  activeFilters={selectedTagFilters}
                  ref={tagFilterButtonRef}
                />
              </div>
            </div> */}
          </>
        )}
      </div>
      {isLoading && <Loader />}
      {!isLoading && answers.length > 0 && (
        <ul className='answers-list'>
          {answers.map((ans, index) => (
            <li key={`answer-${index}`}>
              <span>{ans.question}?</span>
              <p>{ans.answer}</p>
            </li>
          ))}
        </ul>
      )}
      {/* {eventsData?.length > 0 && questionId && (
        <div className="events">
          <div className="filter">
            <button className="tag-filter-button" ref={eventButtonRef}>
              Which meetings is this pulling from?
            </button>
            <Dropdown
              isExpand={true}
              data={eventsData}
              setActiveFilters={setSelectedEvent}
              activeFilters={selectedEvent}
              ref={eventButtonRef}
            />
          </div>
        </div>
      )} */}
      {questionId && <Feedback reference_id={questionId} feedback_type={1} />}
      {answers.length > 0 ? (
        <button className='previous-button' onClick={handleAskNewQuestion}>
          Ask new question
        </button>
      ) : (
        <button
          className='previous-button'
          onClick={fetctPreviousQuestionsData}
        >
          See previous questions
        </button>
      )}
    </div>
  )
}

export default Ask
